import { Col, Table, TableColumnsType, TablePaginationConfig } from "antd";
import { HTMLAttributes, useEffect, useState } from "react";
import config from "../../config";
import useTableDataAndColumns from "../../features/tables/useTableDataAndColumns";
import useTablePagination from "../../features/tables/useTablePagination";
import TableSearchBar from "./components/TableSearchBar";
import spacing from "../ui/spacingConfig";

export type GenericTableProps<Model> = {
  results: Model[];
  count: number;
  fetchData: (queryParams?: string) => void;
  isLoading: boolean;
  columns: TableColumnsType<Model>;
  onRow?: (record: Model) => HTMLAttributes<Model>;
  direction?: ["ascend" | "descend"];
  dontFetchOnMount?: boolean;
  onSearchChangedFromProps?: (search: string) => void;
};

const getPaginationOptions = (count: number): TablePaginationConfig => ({
  pageSize: config.tablePageSize,
  total: count || 0,
});

export default function GenericTable<Model>({
  fetchData,
  results,
  count,
  isLoading,
  columns,
  onRow,
  direction,
  dontFetchOnMount,
  onSearchChangedFromProps,
}: GenericTableProps<Model>) {
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch on mount
  useEffect(() => {
    if (dontFetchOnMount) {
      setHasFetched(true);
      return;
    }
    const fetchDataOnMount = async () => {
      // We faced on cache issue, so we add a random key to the url
      await fetchData(`key_needed_to_fetch_data_on_mount=${Math.random()}`);
      setHasFetched(true);
    };
    fetchDataOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { onSearchChanged, isSearching, search, onPaginationChange } =
    useTablePagination<Model>(fetchData);

  const { finalDataSource, finalColumns } = useTableDataAndColumns<Model>(
    results,
    columns,
    hasFetched,
    isLoading,
  );

  useEffect(() => {
    if (onSearchChangedFromProps) {
      onSearchChangedFromProps(search?.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <Col span={spacing.m}>
      <TableSearchBar
        onSearch={onSearchChanged}
        isSearching={isSearching}
        disabled={isLoading}
      />
      <Table<Model>
        onRow={onRow}
        columns={finalColumns}
        dataSource={finalDataSource}
        rowKey="id"
        pagination={getPaginationOptions(count)}
        onChange={onPaginationChange}
        loading={false} // Prevents spinner by default
        showHeader={true} // Show Header even in Skeleton mode
        className="table"
        sortDirections={direction}
      />
    </Col>
  );
}
